"use client";

import { cn } from "@/lib/cn";

export interface PoiItemProps {
  title: string;
  subtitle: string;
  imageUrl: string | null;
  imageFitClass: string;
  isActive: boolean;
  onSelect: () => void;
  onHoverOn: () => void;
  onHoverOff: () => void;
}

/** One POI as a row: thumbnail on the left, title and subtitle beside it. */
export function PoiListItem({
  title,
  subtitle,
  imageUrl,
  imageFitClass,
  isActive,
  onSelect,
  onHoverOn,
  onHoverOff,
}: PoiItemProps) {
  return (
    <button
      type="button"
      onClick={onSelect}
      onMouseEnter={onHoverOn}
      onMouseLeave={onHoverOff}
      onFocus={onHoverOn}
      onBlur={onHoverOff}
      className={cn(
        "grid animate-fadeIn cursor-pointer grid-cols-[auto,1fr] items-center border-b",
        "border-opacity-10 text-start md:flex-row",
        "hover:bg-[color:var(--primary-opacity-13)]",
        "dark:border-b-0 dark:bg-dark-color dark:hover:bg-[color:var(--primary-opacity-13)]",
        isActive ? "bg-[color:var(--primary-opacity-13)]" : "bg-white"
      )}
    >
      {imageUrl ? (
        // Tenant-uploaded, arbitrary dimensions, same-origin.
        // eslint-disable-next-line @next/next/no-img-element
        <img
          className={cn("h-14 w-14 rounded", imageFitClass)}
          src={imageUrl}
          alt=""
          // A page of results is a page of resizes; only ask for the ones the
          // reader actually scrolls to.
          loading="lazy"
        />
      ) : (
        // Holds the column so rows with and without an image still line up.
        <div className="h-14 w-14 rounded" />
      )}

      <div className="grid justify-between p-4 leading-normal">
        <h5
          className="overflow-hidden text-ellipsis whitespace-nowrap text-lg font-bold text-black dark:text-off-white"
          title={title}
        >
          {title}
        </h5>
        <p className="text-base text-black dark:text-off-white">{subtitle}</p>
      </div>
    </button>
  );
}
